import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Button, Center, Spinner, Stack, Text } from '@chakra-ui/react';
import { SCHEDULE_SUBSCRIPTION_DOWNGRADE } from '@src/gql/mutations/SubscriptionPlanChange';
import { ACTIVE_TEAM_SEATS, SUBSCRIPTION_DOWNGRADE_QUOTE } from '@src/gql/queries/MembershipPlanChange';
import type { ScoAdvisorEngagement, ScoProduct, ScoSubscriptionChangeQuote } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useMemo, useState } from 'react';
import { PendingPlanChangeBanner } from '../PendingPlanChangeBanner';
import { PlanDowngradeReview, type PlanDowngradeSeat } from '../PlanDowngradeReview';
import { membershipTermLabel } from '../offers';
import { planChangeEndingLines, renewalPriceLabel } from '../planChange';
import { productLabel, productRenewalLabel } from '../presentation';
import { useMembershipPlanChange } from './useMembershipPlanChange';
export type ScheduleDowngradeReviewProps = {
  /** The lower plan, priced from the catalog (fallback when the quote omits the renewal). */
  product: ScoProduct;
  /** Only when the member chose a period; otherwise the server keeps their current term. */
  termId?: string | null;
  onScheduled: (effectiveAt: string | null) => void;
  onClose: () => void;
};

/** Team seats → names, the way My Advisors labels a seat (advisor's name, then the engagement's). */
export function teamSeatsFromEngagements(engagements?: Array<Pick<ScoAdvisorEngagement, 'id' | 'advisorName' | 'advisorUser' | 'category'> | null> | null): PlanDowngradeSeat[] {
  return (engagements || []).flatMap(engagement => {
    if (!engagement) {
      return [];
    }
    const name = engagement.advisorUser?.fullName?.trim() || engagement.advisorUser?.displayName?.trim() || engagement.advisorName?.trim() || 'Advisor';
    return [{
      id: engagement.id,
      name,
      role: engagement.category?.title || null
    }];
  });
}

/**
 * A downgrade at the end of the term: Team → Pro (W4), or a shorter billing term (L57, e.g.
 * Team yearly → Team monthly). Loads the subscription it applies to, the end-of-term quote and,
 * for Team → Pro, the Team seats, then schedules the change. A change already scheduled shows
 * the pending banner (Keep Team / Keep current plan) instead.
 */
export const ScheduleDowngradeReview = ({
  onClose,
  onScheduled,
  product,
  termId
}: ScheduleDowngradeReviewProps) => {
  const {
    refetchCurrentUser
  } = useCurrentUser();
  const planChange = useMembershipPlanChange();
  const downgrade = planChange.downgradeSourceFor(product.productId, termId);
  const source = downgrade?.source || null;
  const subscriptionId = source?.subscriptionId || null;
  // Seats and plan lines end only on a move to a lower plan; a term change keeps them.
  const lowerPlan = downgrade?.kind === 'plan';
  const termOnly = downgrade?.kind === 'term';
  const quoteQuery = useQuery(SUBSCRIPTION_DOWNGRADE_QUOTE, {
    skip: !subscriptionId,
    fetchPolicy: 'network-only',
    variables: {
      subscriptionId: subscriptionId || '',
      productId: product.productId,
      termId: termId || undefined
    }
  });
  const quote = (quoteQuery.data?.scoSubscriptionChangeQuote || null) as ScoSubscriptionChangeQuote | null;
  const seatsQuery = useQuery(ACTIVE_TEAM_SEATS, {
    skip: !subscriptionId || !lowerPlan,
    fetchPolicy: 'network-only'
  });
  const seats = useMemo(() => lowerPlan ? teamSeatsFromEngagements(seatsQuery.data?.advisorEngagements?.items) : [], [lowerPlan, seatsQuery.data?.advisorEngagements?.items]);
  const [schedule, scheduleState] = useMutation(SCHEDULE_SUBSCRIPTION_DOWNGRADE);
  const [error, setError] = useState<string | null>(null);
  if (planChange.loading) {
    return <Center minH={'16rem'}>
        <Spinner aria-label={'Loading your plan'} />
      </Center>;
  }
  if (planChange.pending) {
    return <Stack gap={4}>
        <PendingPlanChangeBanner error={planChange.keepError} keeping={planChange.keeping} pending={planChange.pending} onKeep={() => planChange.keepCurrentPlan()} />
        <Button alignSelf={'flex-start'} variant={'outline'} onClick={onClose}>
          Close
        </Button>
      </Stack>;
  }
  if (!source || !subscriptionId) {
    return <Stack gap={4}>
        <Alert.Root status={'info'}>
          <Alert.Indicator />
          <Text>
            {planChange.error ? 'We couldn’t load your plan right now. Please try again in a moment.' : 'There’s no plan to switch at renewal. A membership that is set to cancel ends at its renewal date instead.'}
          </Text>
        </Alert.Root>
        <Button alignSelf={'flex-start'} variant={'outline'} onClick={onClose}>
          Close
        </Button>
      </Stack>;
  }
  const currentPlanName = source.product?.name?.trim() || 'your current plan';
  const quotedProduct = quote?.product || null;
  const quotedTermId = quotedProduct?.effectiveTermId || quotedProduct?.termId || termId || null;
  return <PlanDowngradeReview currentPlanName={currentPlanName} currentProductId={source.productId} droppedCouponIds={quote?.droppedCouponIds} effectiveAt={quote?.effectiveAt || source.nextAssessmentAt || source.currentPeriodEndsAt} endingLines={lowerPlan ? planChangeEndingLines(source.productId, product.productId) : []} endingSeats={seats} error={error} loading={scheduleState.loading} quoteError={quoteQuery.error ? 'We couldn’t price your renewal right now.' : null} quoteLoading={quoteQuery.loading} seatsLoading={lowerPlan && seatsQuery.loading} targetPlanName={quotedProduct?.name?.trim() || productLabel(product)} targetTermId={quotedTermId} termLabel={quotedTermId ? membershipTermLabel(quotedTermId) : null} termOnly={termOnly} variant={'plain'} renewalPrice={renewalPriceLabel(quote?.nextBillingAmountInCents, quotedProduct, productRenewalLabel(product))} seatsError={lowerPlan && seatsQuery.error ? 'We couldn’t list your advisor seats. All Team seats end at the change.' : null} onCancel={onClose} onRetryQuote={() => quoteQuery.refetch()} onConfirm={async () => {
    setError(null);
    let effectiveAt: string | null = quote?.effectiveAt || null;
    try {
      const result = await schedule({
        variables: {
          input: {
            subscriptionId,
            productId: product.productId,
            termId: termId || undefined
          }
        }
      });
      effectiveAt = result.data?.scheduleSubscriptionDowngrade?.subscription?.pendingChangeAt || effectiveAt;
    } catch (err) {
      setError((err as Error)?.message || 'We couldn’t schedule this change. Please try again.');
      return;
    }
    try {
      await Promise.all([refetchCurrentUser?.(), planChange.refetch()]);
    } catch {
      // The mutation already wrote the pending change into the cache.
    }
    onScheduled(effectiveAt);
  }} />;
};